import * as React from 'react'

import { action, computed, makeObservable, observable } from 'mobx'
import { MobXProviderContext, observer } from 'mobx-react'

import { IWorkflowStep, PermitFieldType } from '~/client/graph'
import { Button } from '~/client/src/shared/components/Button/Button'
import {
  CompanyGroupingOption,
  CompanyListSelector,
  useCompanyListSelectorData,
} from '~/client/src/shared/components/CompanyListSelector'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/client/src/shared/components/shadcn-ui/dialog'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '~/client/src/shared/components/shadcn-ui/tabs'
import UserListSelector from '~/client/src/shared/components/UserListSelector/UserListSelector'
import { useUserListSelectorData } from '~/client/src/shared/components/UserListSelector/useUserListSelectorData'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import User from '~/client/src/shared/models/User'

import ByWorkflowUserPicker from './components/ByWorkflowUserPicker'

const UsersTabContent = observer(function UsersTabContent({
  selectedUserIds,
  onUserToggle,
  onToggleAll,
}: {
  selectedUserIds: Set<string>
  onUserToggle: (userId: string) => void
  onToggleAll: (userIds: string[], selectAll: boolean) => void
}) {
  const stores = React.useContext(MobXProviderContext) as any
  const { userMetadata, currentUser, filterData } = useUserListSelectorData({
    projectMembersStore: stores.projectMembersStore,
    userProjectsStore: stores.userProjectsStore,
    companiesStore: stores.companiesStore,
    state: stores.state,
    projectRolesStore: stores.projectRolesStore,
    projectTeamsStore: stores.projectTeamsStore,
    projectTradesStore: stores.projectTradesStore,
    tagsStore: stores.tagsStore,
  })

  // Use limitedList (accepted members only) to match prior UsersDirectory behavior
  const acceptedUsers = stores.projectMembersStore?.limitedList || []

  return (
    <UserListSelector
      selectedUserIds={new Set(selectedUserIds)}
      onUserToggle={onUserToggle}
      onToggleAll={onToggleAll}
      users={acceptedUsers}
      userMetadata={userMetadata}
      currentUser={currentUser}
      filterData={filterData}
    />
  )
})

const CompaniesTabContent = observer(function CompaniesTabContent({
  selectedCompanyIds,
  onCompanyToggle,
  onToggleAll,
}: {
  selectedCompanyIds: Set<string>
  onCompanyToggle: (companyId: string) => void
  onToggleAll: (companyIds: string[], selectAll: boolean) => void
}) {
  const stores = React.useContext(MobXProviderContext) as any
  const { companies, companyMetadata } = useCompanyListSelectorData({
    companiesStore: stores.companiesStore,
    projectTradesStore: stores.projectTradesStore,
  })

  return (
    <CompanyListSelector
      selectedCompanyIds={new Set(selectedCompanyIds)}
      onCompanyToggle={onCompanyToggle}
      onToggleAll={onToggleAll}
      companies={companies}
      companyMetadata={companyMetadata}
      defaultGrouping={CompanyGroupingOption.TRADE}
    />
  )
})

export enum UserFieldPickerTab {
  BY_USERS = 'BY_USERS',
  BY_WORKFLOW = 'BY_WORKFLOW',
  BY_COMPANY = 'BY_COMPANY',
}

interface IProps {
  isOpen: boolean
  title: string

  selectedUsers: User[]
  onUserAdd: (userId: string) => void
  onUserRemove: (userId: string) => void
  /** Commits a save in one call. The company ids are empty unless canPickCompanies. */
  onDiffCommit?: (
    addedUserIds: string[],
    removedUserIds: string[],
    addedFieldIds: string[],
    removedFieldIds: string[],
    addedCompanyIds: string[],
    removedCompanyIds: string[],
  ) => void

  allWorkflowSteps?: IWorkflowStep[]
  selectedFieldIds?: string[]
  allowedFieldTypes?: PermitFieldType[]
  onFieldToggle?: (fieldId: string, isSelected: boolean) => void
  onFieldsToggle?: (fieldIds: string[], isSelected: boolean) => void

  /**
   * Offers a company tab, whose changes onDiffCommit carries: a company stands for every project
   * member in it.
   */
  canPickCompanies?: boolean
  selectedCompanyIds?: string[]
  /** The tab the dialog opens on. */
  initialTab?: UserFieldPickerTab

  onClose: () => void
}

export class UserFieldPickerDialogStore {
  @observable public activeTab: UserFieldPickerTab = UserFieldPickerTab.BY_USERS
  @observable public draftSelectedUserIds: Set<string> = new Set()
  @observable public draftSelectedFieldIds: Set<string> = new Set()
  @observable public draftSelectedCompanyIds: Set<string> = new Set()

  private initialSelectedUserIds: Set<string> = new Set()
  private initialSelectedFieldIds: Set<string> = new Set()
  private initialSelectedCompanyIds: Set<string> = new Set()

  public constructor() {
    makeObservable(this)
  }

  @action.bound
  public initDraft(
    selectedUsers: User[],
    selectedFieldIds: string[],
    selectedCompanyIds: string[] = [],
    initialTab: UserFieldPickerTab = UserFieldPickerTab.BY_USERS,
  ) {
    this.activeTab = initialTab

    const userIds = selectedUsers?.map(u => u.id) || []
    this.initialSelectedUserIds = new Set(userIds)
    this.draftSelectedUserIds = new Set(userIds)

    const fieldIds = selectedFieldIds || []
    this.initialSelectedFieldIds = new Set(fieldIds)
    this.draftSelectedFieldIds = new Set(fieldIds)

    this.initialSelectedCompanyIds = new Set(selectedCompanyIds)
    this.draftSelectedCompanyIds = new Set(selectedCompanyIds)
  }

  @action.bound
  public toggleDraftCompany(companyId: string) {
    if (this.draftSelectedCompanyIds.has(companyId)) {
      this.draftSelectedCompanyIds.delete(companyId)
    } else {
      this.draftSelectedCompanyIds.add(companyId)
    }
  }

  @action.bound
  public setDraftCompanies(companyIds: string[], selectAll: boolean) {
    companyIds.forEach(id => {
      if (selectAll) {
        this.draftSelectedCompanyIds.add(id)
      } else {
        this.draftSelectedCompanyIds.delete(id)
      }
    })
  }

  @action.bound
  public setActiveTab(tab: UserFieldPickerTab) {
    this.activeTab = tab
  }

  @action.bound
  public toggleDraftUser(userId: string) {
    if (this.draftSelectedUserIds.has(userId)) {
      this.draftSelectedUserIds.delete(userId)
    } else {
      this.draftSelectedUserIds.add(userId)
    }
  }

  @action.bound
  public setDraftUsers(userIds: string[], selectAll: boolean) {
    userIds.forEach(id => {
      if (selectAll) {
        this.draftSelectedUserIds.add(id)
      } else {
        this.draftSelectedUserIds.delete(id)
      }
    })
  }

  @action.bound
  public toggleDraftField(fieldId: string, isSelected: boolean) {
    if (isSelected) {
      this.draftSelectedFieldIds.add(fieldId)
    } else {
      this.draftSelectedFieldIds.delete(fieldId)
    }
  }

  @action.bound
  public toggleDraftFields(fieldIds: string[], isSelected: boolean) {
    fieldIds.forEach(id => {
      if (isSelected) {
        this.draftSelectedFieldIds.add(id)
      } else {
        this.draftSelectedFieldIds.delete(id)
      }
    })
  }

  @computed
  public get hasPendingChanges(): boolean {
    if (this.draftSelectedUserIds.size !== this.initialSelectedUserIds.size) {
      return true
    }
    for (const id of this.draftSelectedUserIds) {
      if (!this.initialSelectedUserIds.has(id)) {
        return true
      }
    }

    if (this.draftSelectedFieldIds.size !== this.initialSelectedFieldIds.size) {
      return true
    }
    for (const id of this.draftSelectedFieldIds) {
      if (!this.initialSelectedFieldIds.has(id)) {
        return true
      }
    }

    return (
      this.getAddedCompanyIds().length > 0 ||
      this.getRemovedCompanyIds().length > 0
    )
  }

  public getAddedCompanyIds(): string[] {
    return [...this.draftSelectedCompanyIds].filter(
      id => !this.initialSelectedCompanyIds.has(id),
    )
  }

  public getRemovedCompanyIds(): string[] {
    return [...this.initialSelectedCompanyIds].filter(
      id => !this.draftSelectedCompanyIds.has(id),
    )
  }

  public getAddedUserIds(): string[] {
    return [...this.draftSelectedUserIds].filter(
      id => !this.initialSelectedUserIds.has(id),
    )
  }

  public getRemovedUserIds(): string[] {
    return [...this.initialSelectedUserIds].filter(
      id => !this.draftSelectedUserIds.has(id),
    )
  }

  public getAddedFieldIds(): string[] {
    return [...this.draftSelectedFieldIds].filter(
      id => !this.initialSelectedFieldIds.has(id),
    )
  }

  public getRemovedFieldIds(): string[] {
    return [...this.initialSelectedFieldIds].filter(
      id => !this.draftSelectedFieldIds.has(id),
    )
  }
}

@observer
export default class UserFieldPickerDialog extends React.Component<IProps> {
  private readonly store: UserFieldPickerDialogStore

  public constructor(props: IProps) {
    super(props)
    this.store = new UserFieldPickerDialogStore()
    if (props.isOpen) {
      this.initDraft()
    }
  }

  public componentDidUpdate(prevProps: IProps) {
    if (!prevProps.isOpen && this.props.isOpen) {
      this.initDraft()
    }
  }

  public render() {
    const { isOpen } = this.props
    const { activeTab, setActiveTab, hasPendingChanges } = this.store
    const hasWorkflowSteps = this.hasWorkflowSteps

    return (
      <Dialog
        open={isOpen}
        onOpenChange={(open, event) => {
          if (!open && event?.reason !== 'escape-key') {
            this.handleCancel()
          }
        }}
        disablePointerDismissal
      >
        <DialogContent
          showCloseButton={false}
          className="tw:w-[400px] tw:sm:max-w-[400px] tw:min-h-[700px] tw:max-h-[700px] tw:flex tw:flex-col tw:gap-0 tw:p-0 tw:overflow-hidden"
        >
          <DialogHeader className="tw:p-4 tw:pb-0">
            <DialogTitle className="tw:text-center">
              {this.props.title}
            </DialogTitle>
          </DialogHeader>
          <Tabs
            value={activeTab}
            onValueChange={v => setActiveTab(v as UserFieldPickerTab)}
            className="tw:flex-1 tw:min-h-0 tw:flex tw:flex-col tw:gap-0"
          >
            <TabsList
              variant="line"
              className="tw:w-full tw:justify-center tw:border-b tw:border-border tw:px-4"
            >
              <TabsTrigger value={UserFieldPickerTab.BY_USERS}>
                {`${Localization.translator.byUsers} (${this.selectedUsersCount})`}
              </TabsTrigger>
              {hasWorkflowSteps && (
                <TabsTrigger value={UserFieldPickerTab.BY_WORKFLOW}>
                  {`${Localization.translator.byWorkflowField} (${this.selectedFieldsCount})`}
                </TabsTrigger>
              )}
              {this.hasCompanyTab && (
                <TabsTrigger value={UserFieldPickerTab.BY_COMPANY}>
                  {`${Localization.translator.byCompany} (${this.store.draftSelectedCompanyIds.size})`}
                </TabsTrigger>
              )}
            </TabsList>
            <TabsContent
              value={UserFieldPickerTab.BY_USERS}
              className="tw:min-h-0 tw:overflow-hidden tw:flex tw:flex-col tw:[&>*]:flex-1"
            >
              {this.renderUsersTab()}
            </TabsContent>
            {hasWorkflowSteps && (
              <TabsContent
                value={UserFieldPickerTab.BY_WORKFLOW}
                className="tw:min-h-0 tw:overflow-hidden tw:flex tw:flex-col tw:[&>*]:flex-1"
              >
                {this.renderWorkflowTab()}
              </TabsContent>
            )}
            {this.hasCompanyTab && (
              <TabsContent
                value={UserFieldPickerTab.BY_COMPANY}
                className="tw:min-h-0 tw:overflow-hidden tw:flex tw:flex-col tw:[&>*]:flex-1"
              >
                <CompaniesTabContent
                  selectedCompanyIds={this.store.draftSelectedCompanyIds}
                  onCompanyToggle={this.store.toggleDraftCompany}
                  onToggleAll={this.store.setDraftCompanies}
                />
              </TabsContent>
            )}
          </Tabs>
          <DialogFooter className="tw:mx-0 tw:mb-0 tw:border-t-0">
            <Button variant="outline" onClick={this.handleCancel}>
              {Localization.translator.cancel}
            </Button>
            <Button onClick={this.handleSave} disabled={!hasPendingChanges}>
              {Localization.translator.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  private renderUsersTab(): JSX.Element {
    return (
      <UsersTabContent
        selectedUserIds={this.store.draftSelectedUserIds}
        onUserToggle={this.store.toggleDraftUser}
        onToggleAll={this.store.setDraftUsers}
      />
    )
  }

  private renderWorkflowTab(): JSX.Element {
    const { allWorkflowSteps, allowedFieldTypes } = this.props

    return (
      <ByWorkflowUserPicker
        allWorkflowSteps={allWorkflowSteps}
        selectedFieldIds={this.store.draftSelectedFieldIds}
        allowedFieldTypes={allowedFieldTypes}
        onFieldToggle={this.store.toggleDraftField}
        onFieldsToggle={this.store.toggleDraftFields}
      />
    )
  }

  private handleSave = () => {
    const {
      onUserAdd,
      onUserRemove,
      onDiffCommit,
      onFieldToggle,
      onFieldsToggle,
      onClose,
    } = this.props

    const addedUserIds = this.store.getAddedUserIds()
    const removedUserIds = this.store.getRemovedUserIds()
    const addedFieldIds = this.store.getAddedFieldIds()
    const removedFieldIds = this.store.getRemovedFieldIds()
    const addedCompanyIds = this.store.getAddedCompanyIds()
    const removedCompanyIds = this.store.getRemovedCompanyIds()

    if (onDiffCommit) {
      if (
        addedUserIds.length ||
        removedUserIds.length ||
        addedFieldIds.length ||
        removedFieldIds.length ||
        addedCompanyIds.length ||
        removedCompanyIds.length
      ) {
        onDiffCommit(
          addedUserIds,
          removedUserIds,
          addedFieldIds,
          removedFieldIds,
          addedCompanyIds,
          removedCompanyIds,
        )
      }
    } else {
      addedUserIds.forEach(id => onUserAdd(id))
      removedUserIds.forEach(id => onUserRemove(id))

      if (addedFieldIds.length) {
        if (onFieldsToggle) {
          onFieldsToggle(addedFieldIds, true)
        } else if (onFieldToggle) {
          addedFieldIds.forEach(id => onFieldToggle(id, true))
        }
      }

      if (removedFieldIds.length) {
        if (onFieldsToggle) {
          onFieldsToggle(removedFieldIds, false)
        } else if (onFieldToggle) {
          removedFieldIds.forEach(id => onFieldToggle(id, false))
        }
      }
    }

    onClose()
  }

  private handleCancel = () => {
    this.props.onClose()
  }

  private initDraft() {
    const { selectedUsers, selectedFieldIds, selectedCompanyIds, initialTab } =
      this.props
    this.store.initDraft(
      selectedUsers,
      selectedFieldIds,
      selectedCompanyIds,
      initialTab,
    )
  }

  private get hasCompanyTab(): boolean {
    const { canPickCompanies, onDiffCommit } = this.props
    return !!canPickCompanies && !!onDiffCommit
  }

  private get hasWorkflowSteps(): boolean {
    const { allWorkflowSteps, onFieldToggle, onDiffCommit } = this.props
    return !!allWorkflowSteps?.length && !!(onFieldToggle || onDiffCommit)
  }

  private get selectedUsersCount(): number {
    return this.store.draftSelectedUserIds.size
  }

  private get selectedFieldsCount(): number {
    return this.store.draftSelectedFieldIds.size
  }
}
